import { Chip } from "@heroui/react";
import { useEffect, type ReactNode } from "react";
import { Link } from "react-router";
import { ACTION_LINK_CLASS } from "./actions";
import { PanelStatus } from "./PanelStatus";
import { SITE_TITLE } from "../config/site";

/**
 * The one state of what Politor does not do yet (Ricerca, Gestione alert,
 * Parlamento in profondità, Rielabora con AI): a chip beside a page's title,
 * a label inside a button, a note, a page. No api, no data: the parts are
 * the design's own, taken from the news area.
 */
export const IN_DEVELOPMENT = "In sviluppo";

/** The chip «In sviluppo»: the source chip of a card (news/NewsCard.tsx SourceChip), without its funnel and its link. */
export function InDevelopmentBadge() {
  return (
    <Chip size="sm" className="h-[22px] rounded-2xl bg-accent-soft px-2 py-px text-xs leading-5 font-medium text-accent">
      {IN_DEVELOPMENT}
    </Chip>
  );
}

/**
 * «In sviluppo» inside an action button of the card (Rielabora con AI), after
 * its icon: one thing with the button, never read as the state of the card's
 * source (since 20/10/2026, in place of the chip beside it). Light on the
 * accent: about 4.7:1. Out of the button's name (aria-hidden): the button
 * points to it with aria-describedby, so it is the button's description.
 */
export function InDevelopmentTag({ id }: { id: string }) {
  return (
    <span
      id={id}
      aria-hidden="true"
      className="rounded-full bg-white/10 px-1.5 text-xs leading-4 font-medium text-accent-foreground"
    >
      {IN_DEVELOPMENT}
    </span>
  );
}

type InDevelopmentNoteProps = {
  /** The name of the feature, as the note's first words: «{feature}: in sviluppo.» */
  feature: string;
  /** For aria-controls, when a button shows and hides the note. */
  id?: string;
  /** What follows: when it will come, what to do meanwhile; it may end with a link. */
  children: ReactNode;
};

/** The note «{feature}: in sviluppo. …»: a neutral notice of the card opened in place (PanelStatus role="note", not indented). */
export function InDevelopmentNote({ feature, id, children }: InDevelopmentNoteProps) {
  return (
    <PanelStatus role="note" indent={false} id={id}>
      {feature}: in sviluppo. {children}
    </PanelStatus>
  );
}

type InDevelopmentPageProps = {
  title: string;
  /** The note of the page. */
  children: ReactNode;
};

/** The white panel of the news area (NewsLayout), as the pages without a list of cards use it. */
export function WhitePanel({ children }: { children: ReactNode }) {
  return <main className="mt-2.5 min-w-0 flex-1 bg-white px-[19px] pt-2 pb-12">{children}</main>;
}

/**
 * The page of a section in development: the white panel of the news area
 * (NewsLayout), one section with the heading of the lists (NewsSection) and
 * the chip, the note, and the way back to the news, an action button of the
 * card without its icon (ACTION_LINK_CLASS, actions.ts).
 */
export function InDevelopmentPage({ title, children }: InDevelopmentPageProps) {
  useEffect(() => {
    const before = document.title;
    document.title = `${title} · ${SITE_TITLE}`;
    return () => {
      document.title = before;
    };
  }, [title]);

  return (
    <WhitePanel>
      <div className="flex flex-col gap-[49px]">
        <section className="flex flex-col gap-[15px]">
          {/* the heading's row of the side panels (sidebar/panels.tsx:33, PanelTitle): the chip where the icon stands */}
          <div className="flex items-center gap-2.5">
            <h1 className="text-xl leading-7 font-semibold text-brand">{title}</h1>
            <InDevelopmentBadge />
          </div>
          {children}
          <Link to="/news" className={`${ACTION_LINK_CLASS} self-start`}>
            Torna alle news
          </Link>
        </section>
      </div>
    </WhitePanel>
  );
}
